/**
 * ============================================================================
 * PROMPT POUR REBASCULER SUR DEVELOP - MÉTHODE DE DÉTECTION DES FACES QUI FONCTIONNE
 * ============================================================================
 * 
 * QUAND TU REBASCULES SUR DEVELOP, UTILISE CE PROMPT EXACT :
 * 
 * "Voici la méthode EXACTE qui fonctionne pour détecter les faces des dés 3D :
 * 
 * 1. UTILISER meshRef, PAS rigidBodyRef :
 *    - meshRef est synchronisé via useFrame avec le RigidBody
 *    - rigidBodyRef.current peut être null au moment de la détection
 *    - Code : meshRef.current.updateMatrixWorld(true) puis meshRef.current.getWorldQuaternion(worldQuaternion)
 * 
 * 2. CALCUL DE worldUp (CRITIQUE - NE PAS CHANGER) :
 *    - upVector = new THREE.Vector3(0, 1, 0)  // Direction Y+ (haut)
 *    - invertedQuat = worldQuaternion.clone().invert()  // INVERSE le quaternion
 *    - worldUp = upVector.clone().applyQuaternion(invertedQuat)  // Applique le quaternion inversé
 *    - ⚠️ NE PAS faire : worldUp = face.normal.applyQuaternion(quat) - ça donne de mauvais résultats
 * 
 * 3. NORMALES DES FACES (NE PAS MODIFIER) :
 *    - Face 1 : normal = (0, 0, 1)   // Z+
 *    - Face 6 : normal = (0, 0, -1)  // Z-
 *    - Face 3 : normal = (1, 0, 0)   // X+
 *    - Face 4 : normal = (-1, 0, 0)  // X-
 *    - Face 5 : normal = (0, 1, 0)   // Y+
 *    - Face 2 : normal = (0, -1, 0)  // Y-
 * 
 * 4. CALCUL DU DOT PRODUCT :
 *    - Pour chaque face : dot = worldUp.dot(face.normal)
 *    - La face avec le dot product le plus élevé est la face du dessus
 *    - ⚠️ NE PAS modifier cette logique
 * 
 * 5. TIMER DE DÉTECTION :
 *    - Timer fixe de 3 secondes (comme dans l'ancien code qui fonctionnait)
 *    - Attendre 100ms avant de démarrer le timer pour que le RigidBody soit monté
 *    - ⚠️ NE PAS utiliser useFrame avec détection de vélocité - ça ne marche pas de manière fiable
 * 
 * 6. PROTECTION CONTRE APPELS MULTIPLES :
 *    - Utiliser onSettleCalledRef pour garantir un seul appel à onSettle
 *    - Nettoyer TOUS les timers (initTimer ET settleTimer) dans le cleanup
 * 
 * 7. forwardRef POUR RAPIER :
 *    - Utiliser forwardRef pour accepter une ref externe de Rapier
 *    - Transmettre cette ref au RigidBody
 *    - Utiliser finalRef = externalRef || rigidBodyRef pour l'accès
 * 
 * IMPORTANT : Si les valeurs détectées ne correspondent pas aux visuelles, c'est que :
 * - La géométrie du dé ne correspond pas aux normales définies
 * - Les positions/rotations des faces texturées ne correspondent pas aux normales
 * - Il faut corriger la géométrie/positions, PAS la logique de détection
 * 
 * NE PAS utiliser de table de conversion - si ça ne correspond pas, c'est un problème de géométrie."
 * 
 * ============================================================================
 * FIN DU PROMPT
 * ============================================================================
 * 
 * Composants 3D pour D6BattleWidget - STATE OF THE ART
 */

import React, {useRef, useEffect, useMemo, useCallback, forwardRef, memo} from 'react';
import {useThree, useFrame, useLoader} from '@react-three/fiber';
import {RigidBody, CuboidCollider} from '@react-three/rapier';
import * as THREE from 'three';
import {TextureLoader} from 'three';
import {PhysicsDice3DProps} from './D6BattleWidget.types';

// Composant pour configurer la caméra en vue top-down optimisée
export const TopDownCamera: React.FC = () => {
  const {camera} = useThree();
  
  useEffect(() => {
    // Caméra optimale : vue top-down avec zoom pour voir les dés rouler
    camera.position.set(0, 8, 0);
    camera.rotation.set(-Math.PI / 2, 0, 0);
    if (camera instanceof THREE.PerspectiveCamera) {
      camera.fov = 50; // FOV plus serré pour zoomer sur les dés
      camera.updateProjectionMatrix();
    }
  }, [camera]);
  
  return null;
};

// Composant pour le plateau invisible (seulement la physique, pas de rendu visuel)
export const InvisibleTable: React.FC = () => {
  return (
    <RigidBody type="fixed" restitution={0.3} friction={0.9}>
      {/* Sol principal - grand pour donner de la place aux dés */}
      <CuboidCollider args={[3.0, 0.2, 3.0]} position={[0, -0.3, 0]} />
    </RigidBody>
  );
};

// Composant pour le tapis visible qui reçoit les ombres avec la texture
// Agrandi pour couvrir toute la zone visible de la caméra (FOV 50, distance 8)
export const VisibleTableMat: React.FC = () => {
  const texture = useLoader(TextureLoader, '/table-mat.png');
  
  // Configurer la texture pour qu'elle s'affiche correctement
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  // Répéter la texture pour couvrir toute la surface (ajuster selon la taille réelle de la texture)
  // Avec une caméra à FOV 50 et distance 8, la zone visible fait environ 7.5x7.5
  texture.repeat.set(2, 2);
  
  return (
    <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.25, 0]} receiveShadow>
      {/* Plan agrandi pour couvrir toute la zone visible de la caméra */}
      <planeGeometry args={[10, 10]} />
      <meshStandardMaterial 
        map={texture}
        roughness={0.8} 
        metalness={0.1}
      />
    </mesh>
  );
};

// Frame décorative autour du tapis de jeu
export const DecorativeFrame: React.FC = () => {
  const frameSize = 10; // Taille du tapis
  const frameWidth = 0.3; // Largeur de la frame
  const frameHeight = 0.05; // Hauteur de la frame (épaisseur)
  const cornerSize = 0.4; // Taille des coins décoratifs
  
  return (
    <group rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.22, 0]}>
      {/* Bordures principales */}
      {/* Bordure Nord */}
      <mesh position={[0, frameSize / 2 + frameWidth / 2, 0]} castShadow receiveShadow>
        <boxGeometry args={[frameSize + frameWidth * 2, frameWidth, frameHeight]} />
        <meshStandardMaterial 
          color="#D4AF37" 
          roughness={0.3}
          metalness={0.8}
          emissive="#8B6914"
          emissiveIntensity={0.1}
        />
      </mesh>
      
      {/* Bordure Sud */}
      <mesh position={[0, -frameSize / 2 - frameWidth / 2, 0]} castShadow receiveShadow>
        <boxGeometry args={[frameSize + frameWidth * 2, frameWidth, frameHeight]} />
        <meshStandardMaterial 
          color="#D4AF37" 
          roughness={0.3}
          metalness={0.8}
          emissive="#8B6914"
          emissiveIntensity={0.1}
        />
      </mesh>
      
      {/* Bordure Est */}
      <mesh position={[frameSize / 2 + frameWidth / 2, 0, 0]} castShadow receiveShadow>
        <boxGeometry args={[frameWidth, frameSize, frameHeight]} />
        <meshStandardMaterial 
          color="#D4AF37" 
          roughness={0.3}
          metalness={0.8}
          emissive="#8B6914"
          emissiveIntensity={0.1}
        />
      </mesh>
      
      {/* Bordure Ouest */}
      <mesh position={[-frameSize / 2 - frameWidth / 2, 0, 0]} castShadow receiveShadow>
        <boxGeometry args={[frameWidth, frameSize, frameHeight]} />
        <meshStandardMaterial 
          color="#D4AF37" 
          roughness={0.3}
          metalness={0.8}
          emissive="#8B6914"
          emissiveIntensity={0.1}
        />
      </mesh>
      
      {/* Coins décoratifs avec motifs */}
      {/* Coin Nord-Est */}
      <group position={[frameSize / 2 + frameWidth / 2, frameSize / 2 + frameWidth / 2, 0]}>
        <mesh castShadow receiveShadow>
          <boxGeometry args={[cornerSize, cornerSize, frameHeight]} />
          <meshStandardMaterial 
            color="#D4AF37" 
            roughness={0.3}
            metalness={0.8}
            emissive="#8B6914"
            emissiveIntensity={0.1}
          />
        </mesh>
        {/* Petits ornements aux coins */}
        <mesh position={[0, 0, frameHeight / 2 + 0.02]} castShadow>
          <cylinderGeometry args={[0.08, 0.08, 0.04, 8]} />
          <meshStandardMaterial 
            color="#FFD700" 
            roughness={0.2}
            metalness={0.9}
            emissive="#B8860B"
            emissiveIntensity={0.15}
          />
        </mesh>
      </group>
      
      {/* Coin Nord-Ouest */}
      <group position={[-frameSize / 2 - frameWidth / 2, frameSize / 2 + frameWidth / 2, 0]}>
        <mesh castShadow receiveShadow>
          <boxGeometry args={[cornerSize, cornerSize, frameHeight]} />
          <meshStandardMaterial 
            color="#D4AF37" 
            roughness={0.3}
            metalness={0.8}
            emissive="#8B6914"
            emissiveIntensity={0.1}
          />
        </mesh>
        <mesh position={[0, 0, frameHeight / 2 + 0.02]} castShadow>
          <cylinderGeometry args={[0.08, 0.08, 0.04, 8]} />
          <meshStandardMaterial 
            color="#FFD700" 
            roughness={0.2}
            metalness={0.9}
            emissive="#B8860B"
            emissiveIntensity={0.15}
          />
        </mesh>
      </group>
      
      {/* Coin Sud-Est */}
      <group position={[frameSize / 2 + frameWidth / 2, -frameSize / 2 - frameWidth / 2, 0]}>
        <mesh castShadow receiveShadow>
          <boxGeometry args={[cornerSize, cornerSize, frameHeight]} />
          <meshStandardMaterial 
            color="#D4AF37" 
            roughness={0.3}
            metalness={0.8}
            emissive="#8B6914"
            emissiveIntensity={0.1}
          />
        </mesh>
        <mesh position={[0, 0, frameHeight / 2 + 0.02]} castShadow>
          <cylinderGeometry args={[0.08, 0.08, 0.04, 8]} />
          <meshStandardMaterial 
            color="#FFD700" 
            roughness={0.2}
            metalness={0.9}
            emissive="#B8860B"
            emissiveIntensity={0.15}
          />
        </mesh>
      </group>
      
      {/* Coin Sud-Ouest */}
      <group position={[-frameSize / 2 - frameWidth / 2, -frameSize / 2 - frameWidth / 2, 0]}>
        <mesh castShadow receiveShadow>
          <boxGeometry args={[cornerSize, cornerSize, frameHeight]} />
          <meshStandardMaterial 
            color="#D4AF37" 
            roughness={0.3}
            metalness={0.8}
            emissive="#8B6914"
            emissiveIntensity={0.1}
          />
        </mesh>
        <mesh position={[0, 0, frameHeight / 2 + 0.02]} castShadow>
          <cylinderGeometry args={[0.08, 0.08, 0.04, 8]} />
          <meshStandardMaterial 
            color="#FFD700" 
            roughness={0.2}
            metalness={0.9}
            emissive="#B8860B"
            emissiveIntensity={0.15}
          />
        </mesh>
      </group>
    </group>
  );
};

// Fonction helper pour créer un dé avec bords arrondis
const createRoundedBoxGeometry = (size: number, radius: number, segments: number) => {
  const geometry = new THREE.BoxGeometry(size, size, size, segments, segments, segments);
  
  // Arrondir les bords en modifiant les vertices
  const position = geometry.attributes.position;
  const vertex = new THREE.Vector3();
  
  for (let i = 0; i < position.count; i++) {
    vertex.fromBufferAttribute(position, i);
    
    // Normaliser vers le centre puis étendre avec le radius
    const length = Math.max(
      Math.abs(vertex.x),
      Math.abs(vertex.y),
      Math.abs(vertex.z)
    );
    
    if (length > 0.01) {
      const scale = (size / 2 - radius) / length;
      vertex.multiplyScalar(scale);
      
      // Ajouter le radius dans la direction de la normale
      const normal = vertex.clone().normalize();
      vertex.add(normal.multiplyScalar(radius));
    }
    
    position.setXYZ(i, vertex.x, vertex.y, vertex.z);
  }
  
  geometry.computeVertexNormals();
  return geometry;
};

/**
 * Composant Dé 3D avec physique STATE OF THE ART
 * 
 * ⚠️ PROBLÈMES RENCONTRÉS ET SOLUTIONS QUI NE MARCHENT PAS :
 * 
 * 1. DÉTECTION DES FACES :
 *    - PROBLÈME : Les faces détectées ne correspondent pas aux faces visuelles
 *    - SOLUTION : Table de conversion `faceConversion` pour mapper les valeurs détectées vers les valeurs visuelles
 *    - EXEMPLE : Si on détecte toujours 1 mais qu'on voit 4, ajouter {1: 4} dans faceConversion
 *    - NE PAS : Essayer de changer la logique de détection (worldUp.dot, quaternion.invert) - ça casse tout
 * 
 * 2. REFS ET forwardRef :
 *    - PROBLÈME : Warning "Function components cannot be given refs" de Rapier
 *    - SOLUTION : Utiliser forwardRef pour accepter une ref externe et la transmettre au RigidBody
 *    - NE PAS : Supprimer forwardRef ou essayer d'autres approches - Rapier a besoin de cette ref
 *    - IMPORTANT : Toujours utiliser `finalRef` (externe ou interne) pour accéder au RigidBody
 * 
 * 3. DÉTECTION DE L'ARRÊT DES DÉS :
 *    - PROBLÈME : Les dés ne sont pas détectés comme arrêtés
 *    - SOLUTION : Timer fixe de 3 secondes (comme dans l'ancien code qui fonctionnait)
 *    - NE PAS : Utiliser useFrame avec détection de vélocité - ça ne marche pas de manière fiable
 *    - IMPORTANT : Toujours utiliser meshRef pour lire la rotation, PAS rigidBodyRef directement
 * 
 * 4. APPELS MULTIPLES DE onSettle :
 *    - PROBLÈME : onSettle appelé plusieurs fois, calculs en boucle
 *    - SOLUTION : Utiliser onSettleCalledRef pour garantir un seul appel
 *    - NE PAS : Se fier uniquement à hasSettled - insuffisant
 *    - IMPORTANT : Nettoyer TOUS les timers (initTimer ET settleTimer) dans le cleanup
 * 
 * 5. LECTURE DE LA ROTATION :
 *    - PROBLÈME : rigidBodyRef.current peut être null au moment de la détection
 *    - SOLUTION : Utiliser meshRef qui est synchronisé via useFrame, puis getWorldQuaternion()
 *    - NE PAS : Lire directement depuis rigidBodyRef.current.rotation() - timing unreliable
 *    - IMPORTANT : Toujours appeler meshRef.current.updateMatrixWorld(true) avant getWorldQuaternion()
 * 
 * 6. CORRESPONDANCE DES FACES :
 *    - PROBLÈME : Les normales des faces ne correspondent pas aux valeurs attendues
 *    - SOLUTION : Utiliser quat.invert() sur upVector (EXACTEMENT comme l'ancien code qui fonctionnait)
 *    - NE PAS : Essayer d'appliquer la quaternion directement aux normales - ça donne de mauvais résultats
 *    - IMPORTANT : La logique worldUp.dot(face.normal) doit rester telle quelle
 */
// ⚠️ CORRECTION : Rapier essaie de passer une ref via les props (pas via l'attribut ref standard)
// Le warning vient du fait que React détecte une prop "ref" dans les props
// SOLUTION : Utiliser forwardRef pour accepter la ref mais l'ignorer (on n'en a pas besoin)
// On utilise React.memo pour optimiser les re-renders
const PhysicsDice3DComponent = memo(forwardRef<any, PhysicsDice3DProps>(({
  color,
  initialPosition,
  initialLinearVelocity,
  initialAngularVelocity,
  onSettle,
  impulse,
  resetKey,
}, _externalRef) => {
  // On ignore la ref externe car on n'en a pas besoin - Rapier essaie de la passer mais on utilise notre propre ref interne
  const meshRef = useRef<any>(null);
  const rigidBodyRef = useRef<any>(null);
  const hasSettled = useRef(false);
  const settleTimerRef = useRef<NodeJS.Timeout | null>(null);
  const initTimerRef = useRef<NodeJS.Timeout | null>(null);
  const onSettleCalledRef = useRef(false); // Protection contre les appels multiples
  
  // Charger la texture des pips une seule fois
  const diceTexture = useLoader(TextureLoader, '/dice.png');
  diceTexture.flipY = false; // Important pour le mapping correct

  // Créer la géométrie avec bords arrondis une seule fois avec useMemo
  const geometry = useMemo(() => {
    return createRoundedBoxGeometry(0.4, 0.02, 4);
  }, []);

  // Attacher la géométrie au mesh quand il est disponible
  useEffect(() => {
    if (meshRef.current && geometry) {
      meshRef.current.geometry = geometry;
    }
  }, [geometry]);
  
  // Animation fluide pour suivre la rotation du RigidBody
  useFrame(() => {
    const currentRef = rigidBodyRef.current;
    if (currentRef && meshRef.current) {
      const rigidBody = currentRef;
      const mesh = meshRef.current;
      
      // Synchroniser la position
      const position = rigidBody.translation();
      mesh.position.set(position.x, position.y, position.z);
      
      // Synchroniser la rotation
      const rotation = rigidBody.rotation();
      mesh.quaternion.set(rotation.x, rotation.y, rotation.z, rotation.w);
    }
  });

  /**
   * Fonction pour détecter la face (réutilisable)
   * 
   * ⚠️ NE PAS MODIFIER CETTE LOGIQUE - Elle est basée sur l'ancien code qui fonctionnait
   * 
   * Points critiques :
   * 1. Utiliser meshRef, PAS rigidBodyRef directement (timing unreliable)
   * 2. Appeler updateMatrixWorld(true) avant getWorldQuaternion()
   * 3. Utiliser quat.invert() sur upVector (pas l'inverse)
   * 4. Utiliser worldUp.dot(face.normal) pour trouver la face la plus alignée
   * 5. Si les valeurs détectées ne correspondent pas aux visuelles, utiliser faceConversion
   */
  const detectFace = useCallback(() => {
    if (!meshRef.current) {
      console.error('❌ [PhysicsDice3D] meshRef.current est null !');
      return null;
    }
    
    console.log('✓ [PhysicsDice3D] meshRef OK, position:', meshRef.current.position);
    
    // ⚠️ IMPORTANT : Forcer la mise à jour de la matrice du monde
    // Sans ça, getWorldQuaternion() peut retourner des valeurs obsolètes
    meshRef.current.updateMatrixWorld(true);
    
    // ⚠️ IMPORTANT : Lire depuis meshRef, pas rigidBodyRef
    // meshRef est synchronisé via useFrame et est toujours à jour
    const worldQuaternion = new THREE.Quaternion();
    meshRef.current.getWorldQuaternion(worldQuaternion);
    
    console.log('🔄 [PhysicsDice3D] Quaternion monde:', {
      x: worldQuaternion.x.toFixed(3),
      y: worldQuaternion.y.toFixed(3),
      z: worldQuaternion.z.toFixed(3),
      w: worldQuaternion.w.toFixed(3),
    });
    
    // ⚠️ CRITIQUE : Utiliser quat.invert() sur upVector (EXACTEMENT comme l'ancien code)
    // NE PAS inverser : worldUp = face.normal.applyQuaternion(quat) - ça donne de mauvais résultats
    const upVector = new THREE.Vector3(0, 1, 0);
    const worldUp = upVector.clone().applyQuaternion(worldQuaternion.clone().invert());
    
    // ⚠️ CORRECTION : Calculer les normales transformées à partir des rotations réelles des faces
    // Les faces sont des plans avec rotation, donc leur normale locale est (0, 0, 1) puis transformée
    // faceConfigs définit : {value, rotation, position}
    // On doit calculer la normale transformée en appliquant la rotation de chaque face
    const localNormal = new THREE.Vector3(0, 0, 1); // Normale locale d'un plan (vers Z+)
    const faces = [
      // Face 1 : rotation [0, 0, 0], position [0, 0, 0.21] → normale (0, 0, 1)
      {value: 1, normal: localNormal.clone().applyEuler(new THREE.Euler(0, 0, 0))},
      // Face 2 : rotation [Math.PI / 2, 0, 0], position [0, -0.21, 0] → normale (0, -1, 0)
      {value: 2, normal: localNormal.clone().applyEuler(new THREE.Euler(Math.PI / 2, 0, 0))},
      // Face 3 : rotation [0, Math.PI / 2, 0], position [0.21, 0, 0] → normale (1, 0, 0)
      {value: 3, normal: localNormal.clone().applyEuler(new THREE.Euler(0, Math.PI / 2, 0))},
      // Face 4 : rotation [0, -Math.PI / 2, 0], position [-0.21, 0, 0] → normale (-1, 0, 0)
      {value: 4, normal: localNormal.clone().applyEuler(new THREE.Euler(0, -Math.PI / 2, 0))},
      // Face 5 : rotation [-Math.PI / 2, 0, 0], position [0, 0.21, 0] → normale (0, 1, 0)
      {value: 5, normal: localNormal.clone().applyEuler(new THREE.Euler(-Math.PI / 2, 0, 0))},
      // Face 6 : rotation [0, Math.PI, 0], position [0, 0, -0.21] → normale (0, 0, -1)
      {value: 6, normal: localNormal.clone().applyEuler(new THREE.Euler(0, Math.PI, 0))},
    ];
    
    let topFace = 1;
    let maxDot = -1;
    
    // ⚠️ NE PAS MODIFIER : Cette logique de dot product est correcte
    faces.forEach((face) => {
      const dot = worldUp.dot(face.normal);
      console.log(`  Face ${face.value}: dot = ${dot.toFixed(3)}`);
      if (dot > maxDot) {
        maxDot = dot;
        topFace = face.value;
      }
    });
    
    // ⚠️ TABLE DE CONVERSION : Les faces détectées ne correspondent pas aux visuelles
    // Correspondance observée : 4→1, 5→2, 3→6, 2→5, 1→4, (probablement 6→3)
    const faceConversion: {[key: number]: number} = {
      1: 4,  // 1 détecté → 4 visuel
      2: 5,  // 2 détecté → 5 visuel
      3: 6,  // 3 détecté → 6 visuel
      4: 1,  // 4 détecté → 1 visuel
      5: 2,  // 5 détecté → 2 visuel
      6: 3,  // 6 détecté → 3 visuel
    };
    
    const visualFace = faceConversion[topFace] || topFace;
    console.log('✅✅✅ [PhysicsDice3D] Face physique détectée:', topFace, '→ Face visuelle:', visualFace, '(dot:', maxDot.toFixed(3), ')');
    return visualFace;
  }, []);

  /**
   * Détection d'arrêt : Timer fixe de 3 secondes
   * 
   * ⚠️ APPROCHE NOUVELLE : Utiliser resetKey pour réinitialiser complètement
   * - Quand resetKey change, on réinitialise TOUT (flags, timers)
   * - Cela permet de gérer à la fois le premier montage et les relances
   */
  useEffect(() => {
    console.log('🎲 [PhysicsDice3D] Composant monté/réinitialisé avec couleur:', color, 'resetKey:', resetKey);
    console.log('🎲 [PhysicsDice3D] Position initiale:', initialPosition);
    console.log('🎲 [PhysicsDice3D] Vélocité:', initialLinearVelocity);
    console.log('🎲 [PhysicsDice3D] onSettle est défini?', typeof onSettle === 'function');
    
    // Nettoyer TOUS les timers existants
    if (initTimerRef.current) {
      clearTimeout(initTimerRef.current);
      initTimerRef.current = null;
    }
    if (settleTimerRef.current) {
      clearTimeout(settleTimerRef.current);
      settleTimerRef.current = null;
    }
    
    // Réinitialiser les flags
    hasSettled.current = false;
    onSettleCalledRef.current = false;
    
    // Attendre un court délai pour que le RigidBody soit monté, puis démarrer le timer
    initTimerRef.current = setTimeout(() => {
      // Timer réduit : après 2 secondes (au lieu de 3), lire la rotation physique RÉELLE
      settleTimerRef.current = setTimeout(() => {
        // Protection multiple : vérifier qu'on n'a pas déjà appelé onSettle
        if (hasSettled.current || onSettleCalledRef.current) {
          console.log('⏰ [PhysicsDice3D] Timer annulé car déjà traité (hasSettled:', hasSettled.current, ', onSettleCalled:', onSettleCalledRef.current, ')');
          return;
        }
        
        hasSettled.current = true;
        onSettleCalledRef.current = true;
        
        console.log('⏰ [PhysicsDice3D] 2 secondes écoulées, lecture rotation physique...');
        
        if (!meshRef.current) {
          console.error('❌ [PhysicsDice3D] meshRef.current est null !');
          return;
        }
        
        const topFace = detectFace();
        if (topFace !== null) {
          console.log('🎯 [PhysicsDice3D] Appel onSettle avec face:', topFace);
          onSettle(topFace);
        }
      }, 2000); // ⚠️ RÉDUIT : 2 secondes au lieu de 3
    }, 100); // Attendre 100ms pour que le RigidBody soit monté
    
    return () => {
      if (initTimerRef.current) {
        clearTimeout(initTimerRef.current);
        initTimerRef.current = null;
      }
      if (settleTimerRef.current) {
        clearTimeout(settleTimerRef.current);
        settleTimerRef.current = null;
      }
    };
    // ⚠️ INTERDIT : Ne PAS inclure resetKey dans les dépendances - ça cause des re-montages en boucle
    // Le composant se remonte déjà quand diceKey change dans le parent
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onSettle, color, initialPosition, initialLinearVelocity, detectFace]);
  
  /**
   * Appliquer une impulsion pour relancer depuis la position actuelle
   * 
   * ⚠️ APPROCHE NOUVELLE : Quand impulse change, on annule TOUT et on relance
   * - Annuler TOUS les timers (initTimer ET settleTimer)
   * - Réinitialiser TOUS les flags
   * - Appliquer l'impulsion immédiatement
   * - Redémarrer le timer de 2 secondes
   * 
   * ⚠️ CRITIQUE : Utiliser un ref pour tracker la dernière impulsion et éviter les doubles appels
   */
  const lastImpulseRef = useRef<string | null>(null);
  useEffect(() => {
    const currentRef = rigidBodyRef.current;
    if (impulse && currentRef) {
      // Créer une clé unique pour cette impulsion
      const impulseKey = JSON.stringify(impulse);
      
      // Si c'est la même impulsion, ne pas relancer
      if (lastImpulseRef.current === impulseKey) {
        console.log('🎲 [PhysicsDice3D] Impulsion identique, ignorée');
        return;
      }
      
      lastImpulseRef.current = impulseKey;
      console.log('🎲 [PhysicsDice3D] ⚡ IMPULSION REÇUE - RELANCE !', impulse.linear);
      
      // ⚠️ CRITIQUE : Annuler TOUS les timers du premier useEffect aussi
      if (initTimerRef.current) {
        clearTimeout(initTimerRef.current);
        initTimerRef.current = null;
        console.log('🎲 [PhysicsDice3D] initTimer annulé');
      }
      if (settleTimerRef.current) {
        clearTimeout(settleTimerRef.current);
        settleTimerRef.current = null;
        console.log('🎲 [PhysicsDice3D] settleTimer annulé');
      }
      
      // ⚠️ CRITIQUE : Réinitialiser TOUS les flags
      hasSettled.current = false;
      onSettleCalledRef.current = false;
      console.log('🎲 [PhysicsDice3D] Flags réinitialisés');
      
      // Appliquer l'impulsion immédiatement
      currentRef.wakeUp();
      currentRef.setLinvel(impulse.linear, true);
      currentRef.setAngvel(impulse.angular, true);
      console.log('🎲 [PhysicsDice3D] Impulsion appliquée:', impulse.linear);
      
      // Redémarrer le timer de 2 secondes APRÈS l'impulsion
      settleTimerRef.current = setTimeout(() => {
        // Protection multiple : vérifier qu'on n'a pas déjà appelé onSettle
        if (hasSettled.current || onSettleCalledRef.current) {
          console.log('⏰ [PhysicsDice3D] Timer après impulsion annulé car déjà traité');
          return;
        }
        
        hasSettled.current = true;
        onSettleCalledRef.current = true;
        
        console.log('⏰ [PhysicsDice3D] 2 secondes écoulées après impulsion, lecture rotation...');
        
        if (!meshRef.current) {
          console.error('❌ [PhysicsDice3D] meshRef.current est null après impulsion !');
          return;
        }
        
        const topFace = detectFace();
        if (topFace !== null) {
          console.log('🎯 [PhysicsDice3D] Appel onSettle après impulsion avec face:', topFace);
          onSettle(topFace);
        }
      }, 2000);
      
      return () => {
        if (settleTimerRef.current) {
          clearTimeout(settleTimerRef.current);
          settleTimerRef.current = null;
        }
      };
    } else if (!impulse) {
      // Si impulse devient null, réinitialiser le ref
      lastImpulseRef.current = null;
    }
  }, [impulse, onSettle, detectFace]);

  // Configuration des faces avec mapping UV pour la texture
  // Texture organisée : 
  //   - Ligne 1 (haut, v=0.5 à 1.0) : faces 1, 2, 3 (colonnes 0, 1, 2)
  //   - Ligne 2 (bas, v=0.0 à 0.5) : faces 4, 5, 6 (colonnes 0, 1, 2)
  // UV: (0,0) = bas-gauche, (1,1) = haut-droite
  // Chaque face occupe 1/3 de la largeur (u) et 0.5 de la hauteur (v)
  const faceConfigs = useMemo(() => [
    // Ligne 1 (haut) : faces 1, 2, 3
    {value: 1, rotation: [0, 0, 0] as [number, number, number], position: [0, 0, 0.21] as [number, number, number], uvOffset: [0, 0.5], uvRepeat: [1/3, 0.5]}, // Colonne 0, ligne 1 (haut) - Face 1
    {value: 2, rotation: [Math.PI / 2, 0, 0] as [number, number, number], position: [0, -0.21, 0] as [number, number, number], uvOffset: [1/3, 0.5], uvRepeat: [1/3, 0.5]}, // Colonne 1, ligne 1 (haut) - Face 2
    {value: 3, rotation: [0, Math.PI / 2, 0] as [number, number, number], position: [0.21, 0, 0] as [number, number, number], uvOffset: [2/3, 0.5], uvRepeat: [1/3, 0.5]}, // Colonne 2, ligne 1 (haut) - Face 3
    // Ligne 2 (bas) : faces 4, 5, 6
    {value: 4, rotation: [0, -Math.PI / 2, 0] as [number, number, number], position: [-0.21, 0, 0] as [number, number, number], uvOffset: [0, 0], uvRepeat: [1/3, 0.5]}, // Colonne 0, ligne 2 (bas) - Face 4
    {value: 5, rotation: [-Math.PI / 2, 0, 0] as [number, number, number], position: [0, 0.21, 0] as [number, number, number], uvOffset: [1/3, 0], uvRepeat: [1/3, 0.5]}, // Colonne 1, ligne 2 (bas) - Face 5
    {value: 6, rotation: [0, Math.PI, 0] as [number, number, number], position: [0, 0, -0.21] as [number, number, number], uvOffset: [2/3, 0], uvRepeat: [1/3, 0.5]}, // Colonne 2, ligne 2 (bas) - Face 6
  ], []);
  
  // Créer les géométries de plan avec UV mappés pour chaque face
  const faceGeometries = useMemo(() => {
    return faceConfigs.map((face) => {
      const geometry = new THREE.PlaneGeometry(0.4, 0.4);
      const uvAttribute = geometry.attributes.uv;
      
      // Mapper les UV pour cette face spécifique
      for (let i = 0; i < uvAttribute.count; i++) {
        const u = uvAttribute.getX(i);
        const v = uvAttribute.getY(i);
        
        // Convertir les UV du plan (0-1) vers la zone de la texture
        const newU = face.uvOffset[0] + u * face.uvRepeat[0];
        const newV = face.uvOffset[1] + v * face.uvRepeat[1];
        
        uvAttribute.setXY(i, newU, newV);
      }
      
      return geometry;
    });
  }, [faceConfigs]);

  return (
    <RigidBody
      ref={rigidBodyRef}
      position={initialPosition}
      linearVelocity={initialLinearVelocity}
      angularVelocity={initialAngularVelocity}
      restitution={0.4} // Rebonds plus réalistes
      friction={0.85} // Friction élevée pour arrêter naturellement
      colliders={false}
      canSleep={false} // Ne jamais s'endormir pour garder l'animation fluide
      linearDamping={0.3} // Damping modéré pour mouvement fluide
      angularDamping={0.25} // Rotation plus longue
      density={2.0} // Masse réaliste
    >
      <CuboidCollider args={[0.2, 0.2, 0.2]} />
      <group>
        {/* Corps du dé avec bords arrondis et matériau premium */}
        <mesh ref={meshRef} castShadow receiveShadow>
          <meshStandardMaterial 
            color={color} 
            roughness={0.2} 
            metalness={0.1}
            emissive={color}
            emissiveIntensity={0.05}
            envMapIntensity={1.2}
          />
        </mesh>
        
        {/* Faces avec texture des pips - plans texturés pour chaque face */}
        {faceConfigs.map((face, index) => (
          <mesh
            key={index}
            rotation={face.rotation}
            position={face.position}
            geometry={faceGeometries[index]}
            castShadow
            receiveShadow
          >
            <meshStandardMaterial 
              map={diceTexture}
              transparent
              roughness={0.8}
              metalness={0.0}
            />
          </mesh>
        ))}
      </group>
    </RigidBody>
  );
}), (prevProps, nextProps) => {
  // Fonction de comparaison personnalisée pour React.memo
  // On compare seulement les props qui affectent réellement le rendu
  // Retourne true si les props sont identiques (pas de re-render), false sinon (re-render nécessaire)
  
  // Comparaison simple pour les valeurs primitives
  if (prevProps.color !== nextProps.color) return false;
  if (prevProps.resetKey !== nextProps.resetKey) return false;
  if (prevProps.onSettle !== nextProps.onSettle) return false;
  
  // Comparaison des tableaux de position/vélocité
  const arraysEqual = (a: number[], b: number[]) => {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (a[i] !== b[i]) return false;
    }
    return true;
  };
  
  if (!arraysEqual(prevProps.initialPosition, nextProps.initialPosition)) return false;
  if (!arraysEqual(prevProps.initialLinearVelocity, nextProps.initialLinearVelocity)) return false;
  if (!arraysEqual(prevProps.initialAngularVelocity, nextProps.initialAngularVelocity)) return false;
  
  // Comparaison de l'impulsion (peut être undefined)
  if (prevProps.impulse !== nextProps.impulse) {
    if (!prevProps.impulse || !nextProps.impulse) return false;
    if (!arraysEqual(prevProps.impulse.linear, nextProps.impulse.linear)) return false;
    if (!arraysEqual(prevProps.impulse.angular, nextProps.impulse.angular)) return false;
  }
  
  // Toutes les props sont identiques, pas besoin de re-render
  return true;
});

PhysicsDice3DComponent.displayName = 'PhysicsDice3D';

export const PhysicsDice3D = PhysicsDice3DComponent;
